import React from 'react';
import { useLoanDrawer } from '../useLoanDrawer';
import {
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Amount,
  Text,
  Box,
} from '@razorpay/blade/components';
import { useLoanData } from '../useLoanData';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { LoanDataArraySchemaContract } from 'schemas/LoanDataArraySchema';
import { LOAN_STATUS } from '../constants';
import { AdminLoanDrawer } from 'components/LoanPaymentV2/AdminLoanDrawer';
import { formatDate } from '../utils';
import { getEmiEndMonth } from 'components/LoanPaymentV2/utils';

type ClosedLoan = LoanDataArraySchemaContract['data'][number] & { created_at?: string | null };

export const ClosedLoansTable = () => {
  const { loanId: selectedLoanId, openDrawer, closeDrawer } = useLoanDrawer();
  const { data, isFetching } = useLoanData(LOAN_STATUS.CLOSED);

  const loans = data?.data ?? [];
  const isEmpty = !isFetching && loans.length === 0;

  return (
    <>
      <Table<ClosedLoan>
        data={{ nodes: loans }}
        isLoading={isFetching}
        gridTemplateColumns="1fr 1.2fr 1fr 0.8fr 0.8fr 0.8fr 0.8fr">
        {(tableData) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>ID</TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Reason</TableHeaderCell>
                <TableHeaderCell>EMI End Month</TableHeaderCell>
                <TableHeaderCell textAlign="right">Amount</TableHeaderCell>
                <TableHeaderCell textAlign="right">Outstanding</TableHeaderCell>
                <TableHeaderCell textAlign="right">EMI Per Month</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {tableData.map((loan) => (
                <TableRow key={loan.id} item={loan} onClick={() => openDrawer(loan.id)}>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <Text size="small" weight="semibold">{`#${loan.id}`}</Text>
                      <Text size="xsmall" color="surface.text.gray.muted">
                        {formatDate(loan.created_at)}
                      </Text>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <Text size="small">{loan.name ?? '—'}</Text>
                      {loan.employee_id && (
                        <Text size="xsmall" color="surface.text.gray.muted">
                          {loan.employee_id}
                        </Text>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Text size="small" truncateAfterLines={1}>
                      {loan.remarks ?? '—'}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text size="small">
                      {getEmiEndMonth(loan.recovery_start_from, loan.tenure)}
                    </Text>
                  </TableCell>
                  <TableCell textAlign="right">
                    <Amount value={loan.principal} size="small" />
                  </TableCell>
                  <TableCell textAlign="right">
                    <Amount value={loan.remaining_amount_to_be_paid ?? 0} size="small" />
                  </TableCell>
                  <TableCell textAlign="right">
                    <Amount value={loan.monthly_total_emi ?? 0} size="small" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </>
        )}
      </Table>

      {isEmpty && (
        <EmptyListView
          title="No closed loans"
          description={
            <Text color="surface.text.gray.muted" textAlign="center">
              No closed loans found for your organization.
            </Text>
          }
        />
      )}

      <AdminLoanDrawer
        isOpen={!!selectedLoanId}
        onDismiss={closeDrawer}
        loanId={selectedLoanId}
        showActions={false}
      />
    </>
  );
};
